import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { ListChecks } from "lucide-react";
import { AnimationFrame } from "@/components/common/AnimationFrame";
import { researchApi, errMsg } from "@/lib/research";
import { VerificationBadge } from "@/components/research/AuthorityCard";
import { ExternalLinks } from "@/components/research/CitationVerifier";

const SEV = { BLOCK: "text-danger", WARN: "text-warning", INFO: "text-ink-2" };

export function DraftCitationCheck({ draft, body }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [res, setRes] = useState(null);
  const [checked, setChecked] = useState("");
  const run = async () => {
    setBusy(true);
    try { setRes(await researchApi.citationCheck(draft.id, body)); setChecked(body); } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <>
      <Button variant="outline" size="sm" onClick={() => setOpen(true)} data-testid="draft-citation-check-open"><ListChecks className="mr-1 h-3.5 w-3.5" /> Cite & Consistency</Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-3xl" data-testid="draft-citation-check-dialog">
          <DialogHeader><DialogTitle className="font-display text-2xl">Citation & Consistency Check</DialogTitle><DialogDescription>Citations in the draft are matched against your authority library and uploaded judgments; party names, forum, dates and [verify] placeholders are checked against the matter record. Nothing is altered.</DialogDescription></DialogHeader>
          <Button disabled={busy} onClick={run} className="w-fit" data-testid="draft-citation-check-run">{busy ? "Checking…" : "Check current draft"}</Button>
          {busy && <AnimationFrame slug="collision-matrix" title="Testing authorities against each other" height={220} maxWidth={360} />}
          {res && (
            <div className="max-h-[60vh] space-y-3 overflow-y-auto text-sm" data-testid="draft-citation-check-result">
              <p className="text-ink-2">{res.summary}{body !== checked && <span className="ml-1 text-xs text-warning">· Draft changed since this check</span>}</p>
              <div className="font-semibold text-ink">Citations</div>
              {res.citations.length === 0 && <p className="text-xs text-ink-muted">No recognised citation pattern found in the draft.</p>}
              {res.citations.map((ci, i) => (
                <div key={i} className="rounded-lg border border-line p-3" data-testid={`cite-check-${i}`}>
                  <div className="flex flex-wrap items-center gap-2"><span className="font-serif">{ci.citation}</span><VerificationBadge status={ci.status} />{ci.stance === "CONTRARY" && <span className="rounded-full bg-danger/10 px-1.5 text-[10px] text-danger">marked contrary in library</span>}</div>
                  {ci.title && <div className="text-xs text-ink-2">{ci.title}</div>}
                  {ci.status === "NOT_FOUND_INTERNAL" && <ExternalLinks items={ci.external} className="mt-1" />}
                </div>
              ))}
              <div className="font-semibold text-ink">Consistency with matter record</div>
              {res.consistency.length === 0 && <p className="text-xs text-ink-muted">No inconsistencies detected against parties, forum, dates or placeholders.</p>}
              {res.consistency.map((c, i) => <div key={i} className={cn("rounded-lg border border-line px-3 py-2 text-xs", SEV[c.severity])} data-testid={`consistency-${i}`}><span className="mr-1 rounded bg-muted px-1 text-[10px] text-ink-2">{c.type.replaceAll("_", " ")}</span>{c.detail}</div>)}
              <p className="text-[11px] text-ink-muted">Internal check only — external databases are not connected. Advocate review remains mandatory before filing.</p>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
